/**
 * SignalBox Preferences → Tasks. Admin only.
 *
 * The one place the recurring task list is maintained: add, edit, delete, and
 * set what each task is doing (open / paused / closed). Editing happens here, in
 * a dedicated management screen, NOT on the board — the board is for doing the
 * work (marking complete, adding notes), which readers can do; changing the list
 * is an admin job and lives behind Preferences.
 *
 * The whole task list for a cadence, regardless of period — so a paused or closed
 * task is still reachable here even though it has dropped off the board.
 */
import { useMemo, useState } from 'react'
import * as sb from '../../signalbox/api'
import {
  CADENCES,
  LIFECYCLES,
  REMINDERS,
  type Board,
  type Cadence,
  type DefinitionOut,
  type Lifecycle,
} from '../../signalbox/api'
import { day, dueLabel, fmtDate, fmtStamp, nameFor } from '../../signalbox/status'

interface Props {
  board: Board
  busy: boolean
  act: (fn: () => Promise<unknown>) => Promise<boolean>
  today: Date
}

const RolePill = ({ board, upn }: { board: Board; upn: string }) => {
  const p = board.people.find((x) => x.upn === upn)
  return (
    <span className="role-pill" data-role={p?.role ?? 'none'}>
      {p?.role ?? 'no access'}
    </span>
  )
}

export default function TasksSection({ board, busy, act, today }: Props) {
  const [cadence, setCadence] = useState<Cadence>('monthly')
  const [editing, setEditing] = useState<number | 'new' | null>(null)
  const [showClosed, setShowClosed] = useState(false)

  const all = board.definitions.filter((d) => d.cadence === cadence)
  const defs = showClosed ? all : all.filter((d) => d.lifecycle !== 'closed')
  const closedCount = all.filter((d) => d.lifecycle === 'closed').length

  return (
    <>
      <div className="pill-stack">
        <div className="pill-row">
          <span className="pill-row__label">Cadence</span>
          {CADENCES.map(([id, label]) => (
            <button
              key={id}
              className={'month-pill' + (cadence === id ? ' active' : '')}
              onClick={() => {
                setCadence(id)
                setEditing(null)
              }}
            >
              {label}
              <span className="muted">
                {board.definitions.filter((d) => d.cadence === id && d.lifecycle !== 'closed').length}
              </span>
            </button>
          ))}
        </div>
      </div>

      <p className="tm-scope">
        The recurring tasks Signalbox generates every period. This is where the list is maintained;
        the board is for marking work complete and adding notes, which a reader may do. A task that
        has ever been completed cannot be deleted — close it instead, and the history stays.
      </p>

      <div className="tm-admin-bar">
        <p className="muted" style={{ margin: 0 }}>
          {defs.length} task{defs.length === 1 ? '' : 's'}. Click a task to edit it.
        </p>
        <div className="tm-headactions">
          {closedCount > 0 && (
            <button className="btn btn--small" onClick={() => setShowClosed(!showClosed)}>
              {showClosed ? 'Hide' : 'Show'} {closedCount} closed
            </button>
          )}
          {editing !== 'new' && (
            <button className="btn btn--primary btn--small" onClick={() => setEditing('new')}>
              + Add task
            </button>
          )}
        </div>
      </div>

      {editing === 'new' && (
        <div className="tm-editor">
          <TaskEditor
            key="new"
            board={board}
            busy={busy}
            act={act}
            today={today}
            cadence={cadence}
            def={null}
            onClose={() => setEditing(null)}
          />
        </div>
      )}

      <div className="table-wrap tm-stickyhead">
        <table className="table tm-def-table">
          <thead>
            <tr>
              <th>Task</th>
              <th>Assigned to</th>
              <th>Due</th>
              <th>Status</th>
              <th>Reminder</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {defs.length === 0 && (
              <tr>
                <td colSpan={6} className="muted">
                  No {cadence} tasks{showClosed ? '' : ' — try showing closed ones'}.
                </td>
              </tr>
            )}
            {defs.map((d) => {
              const reminder = REMINDERS.find(([k]) => k === (d.reminder ?? ''))
              const open = editing === d.id
              return (
                <>
                  <tr key={d.id} data-ceased={d.lifecycle === 'closed' ? 'true' : 'false'}>
                    <td className="tm-def-name" onClick={() => setEditing(open ? null : d.id)}>
                      <span className="strong">{d.name}</span>
                      {d.ref && <span className="tm-task-ref">{d.ref}</span>}
                      {d.taskGroup && <span className="tm-task-ref">{d.taskGroup}</span>}
                    </td>
                    <td>
                      {d.assigneeUpn ? (
                        <>
                          {nameFor(board.people, d.assigneeUpn)} <RolePill board={board} upn={d.assigneeUpn} />
                        </>
                      ) : (
                        <span className="muted">Unassigned</span>
                      )}
                    </td>
                    <td>
                      <span className="tm-due-chip" data-type={d.dueType}>
                        {dueLabel(d.due, d.dueType)}
                      </span>
                    </td>
                    <td>
                      <span className="sb-life" data-life={d.lifecycle}>
                        {d.lifecycle}
                      </span>
                    </td>
                    <td>
                      {d.reminder ? (
                        <span title={reminder?.[2]}>✉ {reminder?.[1]}</span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="row-actions">
                      <button className="btn btn--small" onClick={() => setEditing(open ? null : d.id)}>
                        {open ? 'Close' : 'Edit'}
                      </button>
                    </td>
                  </tr>
                  {open && (
                    <tr key={`${d.id}-edit`}>
                      <td colSpan={6} className="tm-editor">
                        <TaskEditor
                          board={board}
                          busy={busy}
                          act={act}
                          today={today}
                          cadence={cadence}
                          def={d}
                          onClose={() => setEditing(null)}
                        />
                      </td>
                    </tr>
                  )}
                </>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ─── The editor ──────────────────────────────────────────────────────────────

interface FormState {
  name: string
  assigneeUpn: string
  due: string
  dueType: string
  cadence: string
  effectiveFrom: string
  ref: string
  instructions: string
  taskGroup: string
  reminder: string
}

function TaskEditor({
  board,
  busy,
  act,
  today,
  cadence,
  def,
  onClose,
}: {
  board: Board
  busy: boolean
  act: (fn: () => Promise<unknown>) => Promise<boolean>
  today: Date
  cadence: Cadence
  def: DefinitionOut | null
  onClose: () => void
}) {
  const isNew = def === null
  const periods = board.periods[(def?.cadence ?? cadence) as Cadence]
  const firstOpen = periods.find((p) => !p.locked)?.id ?? ''

  const [form, setForm] = useState<FormState>(() => ({
    name: def?.name ?? '',
    assigneeUpn: def?.assigneeUpn ?? '',
    due: String(def?.due ?? 5),
    dueType: def?.dueType ?? 'working',
    cadence: def?.cadence ?? cadence,
    effectiveFrom: def?.effectiveFrom ?? firstOpen,
    ref: def?.ref ?? '',
    instructions: def?.instructions ?? '',
    taskGroup: def?.taskGroup ?? '',
    reminder: def?.reminder ?? '',
  }))
  const [err, setErr] = useState<string | null>(null)
  const set = (k: keyof FormState, v: string) => setForm((f) => ({ ...f, [k]: v }))

  const files = def ? board.attachments.filter((a) => a.taskId === def.id) : []

  /* The next still-outstanding occurrence and the last sign-off, derived. */
  const next = useMemo(() => {
    if (!def) return null
    return board.occurrences[def.cadence]
      .filter((o) => o.taskId === def.id && !o.completedAt)
      .sort((a, b) => a.due.localeCompare(b.due))[0]
  }, [def, board])
  const lastDone = useMemo(() => {
    if (!def) return null
    return board.occurrences[def.cadence]
      .filter((o) => o.taskId === def.id && o.completedAt)
      .sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? ''))[0]
  }, [def, board])

  async function save() {
    if (!form.name.trim()) return setErr('A task needs a name.')
    if (form.due === '' || !Number.isInteger(Number(form.due))) {
      return setErr('Due must be a whole number of days.')
    }
    const payload: sb.TaskInput = {
      name: form.name.trim(),
      assigneeUpn: form.assigneeUpn || null,
      due: Number(form.due),
      dueType: form.dueType,
      ref: form.ref.trim() || null,
      instructions: form.instructions.trim() || null,
      taskGroup: form.taskGroup.trim() || null,
      reminder: form.reminder || null,
    }
    const done = isNew
      ? await act(() => sb.createTask({ ...payload, cadence: form.cadence as Cadence, effectiveFrom: form.effectiveFrom }))
      : await act(() => sb.updateTask(def.id, payload))
    if (done) onClose()
  }

  async function remove() {
    if (!def) return
    if (!confirm(`Delete "${def.name}"?`)) return
    const gone = await act(() => sb.deleteTask(def.id))
    if (gone) return onClose()
    if (
      confirm(
        `"${def.name}" has completed history, so deleting it would rewrite periods that are already ` +
          `signed off.\n\nClose it instead? It stops appearing in future periods and the history stays.`
      )
    ) {
      const ok = await act(() => sb.setTaskStatus(def.id, 'closed'))
      if (ok) onClose()
    }
  }

  return (
    <>
      <h4>{isNew ? 'New task' : 'Edit task'}</h4>
      {err && <p className="tm-err">{err}</p>}

      {/* Status — existing tasks only. */}
      {def && (
        <div className="sb-statusrow">
          <span className="sb-edit__label">Status</span>
          <div className="sb-lifebtns">
            {LIFECYCLES.map(([value, label, help]) => (
              <button
                key={value}
                className={'sb-lifebtn' + (def.lifecycle === value ? ' on' : '')}
                data-life={value}
                disabled={busy || def.lifecycle === value}
                title={help}
                onClick={() => act(() => sb.setTaskStatus(def.id, value as Lifecycle))}
              >
                {label}
              </button>
            ))}
            <span className="tm-hint" style={{ marginLeft: '.4rem' }}>
              {LIFECYCLES.find(([v]) => v === def.lifecycle)?.[2]}.
              {next && (
                <>
                  {' '}Next outstanding {fmtDate(day(next.due))}
                  {day(next.due) < today && <span className="sb-late"> overdue</span>}.
                </>
              )}
              {lastDone?.completedAt && (
                <> Last signed off {fmtStamp(lastDone.completedAt)} · {nameFor(board.people, lastDone.completedBy)}.</>
              )}
            </span>
          </div>
        </div>
      )}

      <div className="tm-form">
        <div className="tm-field tm-field--wide">
          <label>Task name</label>
          <input
            className="sched-input"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="e.g. VAT return submitted — NYMR PLC"
          />
        </div>

        <div className="tm-field">
          <label>Owner</label>
          <select className="sched-input" value={form.assigneeUpn} onChange={(e) => set('assigneeUpn', e.target.value)}>
            <option value="">Unassigned</option>
            {board.people.map((p) => (
              <option key={p.upn} value={p.upn}>
                {p.displayName ?? p.upn} ({p.role})
              </option>
            ))}
          </select>
          <span className="tm-hint">From Admin → Users. Reassigning takes effect on every future period.</span>
        </div>

        <div className="tm-field">
          <label>Type</label>
          <select className="sched-input" value={form.cadence} disabled={!isNew} onChange={(e) => set('cadence', e.target.value)}>
            {CADENCES.map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
          {!isNew && (
            <span className="tm-hint">Fixed once the task exists. Close it and add a new one to change it.</span>
          )}
        </div>

        <div className="tm-field">
          <label>Due</label>
          <div className="tm-due-row">
            <input
              className="sched-input sched-input--num"
              type="number"
              value={form.due}
              onChange={(e) => set('due', e.target.value)}
            />
            <select className="sched-input" style={{ minWidth: '9rem' }} value={form.dueType} onChange={(e) => set('dueType', e.target.value)}>
              <option value="working">working days</option>
              <option value="calendar">calendar days</option>
            </select>
            <span className="muted">after period end</span>
          </div>
          <span className="tm-hint">
            Working days skip weekends and England &amp; Wales bank holidays. Negative for work due
            <em> before</em> the period ends.
          </span>
        </div>

        {isNew && (
          <div className="tm-field">
            <label>Takes effect from</label>
            <select className="sched-input" value={form.effectiveFrom} onChange={(e) => set('effectiveFrom', e.target.value)}>
              {periods.map((p) => (
                <option key={p.id} value={p.id} disabled={p.locked}>
                  {p.long}
                  {p.locked ? ' — closed' : p.open ? '' : ' — not open yet'}
                </option>
              ))}
            </select>
            <span className="tm-hint">A task cannot be backdated into a period that is already signed off.</span>
          </div>
        )}

        <div className="tm-field">
          <label>Reference</label>
          <input className="sched-input" value={form.ref} onChange={(e) => set('ref', e.target.value)} placeholder="e.g. NYMR02" />
        </div>

        <div className="tm-field">
          <label>Part of a run</label>
          <input
            className="sched-input"
            value={form.taskGroup}
            onChange={(e) => set('taskGroup', e.target.value)}
            placeholder="e.g. Management accounts"
            list="sb-groups-prefs"
          />
          <datalist id="sb-groups-prefs">
            {[...new Set(board.definitions.map((d) => d.taskGroup).filter(Boolean))].map((g) => (
              <option key={g as string} value={g as string} />
            ))}
          </datalist>
        </div>

        <div className="tm-field tm-field--wide">
          <label>Standing notes</label>
          <textarea
            className="tm-notebox"
            value={form.instructions}
            onChange={(e) => set('instructions', e.target.value)}
            placeholder="Notes about the task itself — shown against every period."
          />
        </div>

        <div className="tm-field tm-field--wide">
          <label>Email Reminder</label>
          <div className="sb-rempills">
            {REMINDERS.map(([value, label, help]) => (
              <button
                key={value || 'none'}
                type="button"
                className={'month-pill' + (form.reminder === value ? ' active' : '')}
                title={help}
                onClick={() => set('reminder', value)}
              >
                {label}
              </button>
            ))}
          </div>
          <span className="tm-hint">
            {form.reminder ? (
              <>
                The owner is emailed from the Finance mailbox{' '}
                {form.reminder === 'digest' ? 'once a day, in one summary.' : (REMINDERS.find(([k]) => k === form.reminder)?.[2] ?? '').toLowerCase() + '.'}{' '}
                Never twice for the same period, never once signed off.
                {!form.assigneeUpn && <> <strong>This task is unassigned, so nothing will be sent.</strong></>}
              </>
            ) : (
              'Nobody is emailed about this task.'
            )}
          </span>
        </div>

        {!isNew && def && (
          <div className="tm-field tm-field--wide">
            <label>Instructions</label>
            <div className="tm-file">
              {files.map((f) => (
                <span key={f.id} className="tm-filechip">
                  <a href={`/api/signalbox/attachments/${f.id}`}>📎 {f.fileName}</a>
                  <button disabled={busy} title="Remove" onClick={() => confirm(`Remove ${f.fileName}?`) && act(() => sb.deleteAttachment(f.id))}>
                    ×
                  </button>
                </span>
              ))}
              <input
                type="file"
                disabled={busy}
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) void act(() => sb.uploadAttachment(def.id, f))
                  e.target.value = ''
                }}
              />
            </div>
            <span className="tm-hint">A method note, a checklist, a screenshot. Up to 10 MB.</span>
          </div>
        )}
      </div>

      <div className="tm-editor__actions">
        <button className="btn btn--primary btn--small" disabled={busy} onClick={save}>
          {isNew ? 'Add task' : 'Save changes'}
        </button>
        <button className="btn btn--small" onClick={onClose}>
          Cancel
        </button>
        <span className="tm-spacer" />
        {!isNew && (
          <button className="btn btn--small tm-danger" disabled={busy} onClick={remove}>
            Delete task
          </button>
        )}
      </div>
    </>
  )
}
